import { Switch, Route, useLocation } from 'wouter';
import { usePrivy } from '@privy-io/react-auth';
import { useAdminAuth } from './contexts/AdminAuthContext';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Referrals from './pages/Referrals';
import Partners from './pages/Partners';
import Payouts from './pages/Payouts';
import FeeTiers from './pages/FeeTiers';
import AuditLog from './pages/AuditLog';
import Jobs from './pages/Jobs';
import LoadingScreen from './components/LoadingScreen';
import LoginScreen from './components/LoginScreen';
import AccessDenied from './components/AccessDenied';

interface AdminLayoutProps {
  walletAddress: string | null;
  onLogout: () => Promise<void>;
}

function AdminLayout({ walletAddress, onLogout }: AdminLayoutProps) {
  const [location] = useLocation();

  return (
    <div className="min-h-screen flex bg-black">
      <Sidebar
        currentPath={location}
        walletAddress={walletAddress || 'Unknown'}
        onLogout={onLogout}
      />

      <main className="flex-1 overflow-auto">
        <Switch>
          <Route path="/" component={Dashboard} />
          <Route path="/users" component={Users} />
          <Route path="/referrals" component={Referrals} />
          <Route path="/partners" component={Partners} />
          <Route path="/payouts" component={Payouts} />
          <Route path="/fee-tiers" component={FeeTiers} />
          <Route path="/jobs" component={Jobs} />
          <Route path="/audit-log" component={AuditLog} />
          <Route>
            <div className="p-8 text-center">
              <p className="text-zinc-400">Page not found</p>
            </div>
          </Route>
        </Switch>
      </main>
    </div>
  );
}

export default function App() {
  const { ready, authenticated, login } = usePrivy();
  const { isAdmin, isLoading, walletAddress, logout, error } = useAdminAuth();

  // Step 1: Wait for Privy and auth check to initialize
  if (!ready || isLoading) {
    return <LoadingScreen />;
  }

  // Step 2: Not authenticated - show login screen
  if (!authenticated) {
    return <LoginScreen onLogin={login} />;
  }

  // Step 3: Authenticated but not admin - show access denied
  if (!isAdmin) {
    return <AccessDenied walletAddress={walletAddress} error={error} onLogout={logout} />;
  }

  // Step 4: Authenticated admin - show admin layout
  return <AdminLayout walletAddress={walletAddress} onLogout={logout} />;
}
